/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import dayjs from "dayjs";
import CopyToClipboard from "react-copy-to-clipboard";
import { Case, Else, If, Switch, Then } from "react-if";
import { Link } from "react-router-dom";
import { Cell } from "react-table";
import styled from "styled-components";

import {
  Button,
  FlexContainer,
  SVGIcon,
  Tag,
  Text,
  theme,
  Tooltip,
} from "@nordcloud/gnui";

import {
  AccessKeyPermissions,
  ApplicationName,
  Role,
} from "~/generated/graphql";

import { ACTION, PopoverControls } from "~/components";
import { dateFormat, SUCCESS_TEXT } from "~/constants";
import { ROUTES } from "~/routing/routes";
import { showSimpleInfo } from "~/services/toast";
import { showRole } from "../UsersAndPermissions/showRole";
import { UpdateApiKeyInput } from "../validators/ApiKeys";

const IconWrapper = styled.div`
  &:hover {
    cursor: pointer;
  }
`;

export const ApiKeysListColumns = (
  removeKey: (apiKeyInfo: { id: string; description: string }) => void,
  rotateKey: (apiKeyInfo: { id: string; description: string }) => void,
  editKey: (item: UpdateApiKeyInput) => void
) => [
  {
    accessor: "expirationDate",
    disableSortBy: true,
    width: "1%",
    Cell: ({ row }: Cell<AccessKeyPermissions>) => {
      const tooltipCaption = `This token is${
        row.original.isExpired ? "" : " not"
      } expired`;

      return (
        <div css={{ position: "relative" }}>
          <Tooltip
            position="left"
            minWidth={row.original.isExpired ? "8rem" : "10rem"}
            caption={tooltipCaption}
            status="notification"
          >
            <div
              data-testid="expiration-icon"
              css={{ paddingLeft: theme.spacing.spacing01 }}
            >
              <If condition={row.original.isExpired}>
                <Then>
                  <SVGIcon
                    data-testid="expired-icon"
                    name="danger"
                    color="danger"
                  />
                </Then>
                <Else>
                  <SVGIcon
                    data-testid="active-icon"
                    name="success"
                    color="success"
                  />
                </Else>
              </If>
            </div>
          </Tooltip>
        </div>
      );
    },
  },
  {
    Header: "Description / ID",
    accessor: "id",
    disableSortBy: true,
    width: "23%",
    Cell: ({ row }: Cell<AccessKeyPermissions>) => (
      <div>
        <Text size="md" css={{ margin: "0" }}>
          {row.original.description || ""}
        </Text>
        <FlexContainer>
          <span
            css={{
              paddingRight: theme.spacing.spacing01,
              color: "grey",
              fontSize: theme.fontSizes.sm,
            }}
          >
            {row.original.id}
          </span>
          <CopyToClipboard
            text={row.original.id}
            onCopy={() => {
              showSimpleInfo(SUCCESS_TEXT.copied);
            }}
          >
            <IconWrapper>
              <SVGIcon name="copy" css={{ fill: "grey" }} />
            </IconWrapper>
          </CopyToClipboard>
        </FlexContainer>
      </div>
    ),
  },
  {
    Header: "Role",
    accessor: "access.role",
    disableSortBy: true,
    width: "9%",
    Cell: ({ row }: Cell<AccessKeyPermissions>) => (
      <Tag text={showRole(row.original.access.role)} />
    ),
  },
  {
    Header: "Permissions",
    accessor: "access.applications",
    disableSortBy: true,
    width: "22%",
    hasLeftBorder: false,
    Cell: ({
      row: {
        original: {
          access: { role, applicationsWithNames },
        },
      },
    }: Cell<AccessKeyPermissions>) => {
      const apps = applicationsWithNames ?? [];
      const appsLength = apps.length;
      const caption = apps.map((app: ApplicationName) => (
        <div key={app.nid}>{app.name}</div>
      ));

      return (
        <Switch>
          <Case condition={role !== Role.TeamLead && !appsLength}>
            <Tag text="All applications" color="notification" />
          </Case>
          <Case condition={role === Role.TeamLead && appsLength < 5}>
            {apps.map((app: ApplicationName) => (
              <Link
                key={app.nid}
                to={`${ROUTES.applications.index}/${app.nid}`}
              >
                <Tag text={app.name ?? ""} color="notification" />
              </Link>
            ))}
          </Case>
          <Case condition={role === Role.TeamLead && appsLength >= 5}>
            <div
              style={{
                marginTop: theme.spacing.spacing01,
                position: "relative",
                maxHeight: theme.spacing.spacing07,
              }}
            >
              <Tooltip minWidth="10rem" status="notification" caption={caption}>
                <Tag
                  text={`${appsLength} applications selected`}
                  color="notification"
                />
              </Tooltip>
            </div>
          </Case>
        </Switch>
      );
    },
  },
  {
    Header: "Expiration / Creation date",
    accessor: "createdAt",
    disableSortBy: true,
    width: "15%",
    Cell: ({ row }: Cell<AccessKeyPermissions>) => (
      <FlexContainer alignItems="flex-start" direction="column">
        <span>
          <If condition={row.original.expirationDate}>
            <Then>
              {dayjs(row.original.expirationDate).format(
                dateFormat.dayMonthYear
              )}
            </Then>
            <Else>Never</Else>
          </If>
        </span>
        <If condition={row.original.createdAt}>
          <Then>
            <Text size="sm" color={theme.color.text.text02}>
              {dayjs(row.original.createdAt).format(dateFormat.dayMonthYear)}
            </Text>
          </Then>
          <Else>
            <div css={{ position: "relative" }}>
              <Tooltip
                status="notification"
                minWidth="10rem"
                caption="This API key was created before the functionality of recording creation date of an API key was  implemented (Nov 7, 2021)"
              >
                <Text size="sm" color={theme.color.text.text02}>
                  Before November 2021
                </Text>
              </Tooltip>
            </div>
          </Else>
        </If>
      </FlexContainer>
    ),
  },
  {
    Header: "",
    accessor: "actions",
    disableSortBy: true,
    hasLeftBorder: false,
    width: "5%",
    Cell: ({ row }: Cell<AccessKeyPermissions>) => {
      const modifiedDescription = row.original.description
        ? `(${row.original.description})`
        : "";
      const id = row.original.id;

      return (
        <PopoverControls
          trigger={
            <Button
              severity="low"
              icon="menu"
              size="md"
              data-testid="apikey-popover-trigger"
            />
          }
          items={[
            {
              label: "Recreate API Key Secret",
              action: ACTION.CUSTOM_TRIGGER,
              customIcon: "gear",
              disabled: row.original.isExpired ?? false,
              onClick: () =>
                rotateKey({
                  id,
                  description: modifiedDescription,
                }),
            },
            {
              label: "Edit API Key",
              action: ACTION.EDIT,
              disabled: row.original.isExpired ?? false,
              onClick: () =>
                editKey({
                  id,
                  input: {
                    description: row.original.description ?? "",
                    role: row.original?.access?.role,
                    applications: row.original?.access?.applications ?? [],
                  },
                }),
            },
            {
              label: "Delete API Key",
              action: ACTION.DELETE,
              onClick: () =>
                removeKey({
                  id,
                  description: modifiedDescription,
                }),
            },
          ]}
        />
      );
    },
  },
];
